// frontend/src/components/AtendimentoForm.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete
} from '@mui/material';
import API_URL from '../config';
import { assertOk } from '../utils/api';

export default function AtendimentoForm({
  token,
  atendente: userAtendente,
  editingAtendimento,
  onAdd,
  onUpdate,
  clearEditing
}) {
  // data local (toISOString usaria UTC e viraria o dia após as 21h no Brasil)
  const now = new Date();
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0')
  ].join('-');
  const [form, setForm] = useState({
    atendente: userAtendente || '',
    dia: today,
    horaInicio: '',
    horaFim: '',
    loja: '',
    contato: '',
    ocorrencia: ''
  });
  const [opts, setOpts] = useState({ lojas: [], contatos: [], ocorrencias: [] });

  // carrega categorias - **não** roda se token for falsy
  useEffect(() => {
    if (!token) return;

    fetch(`${API_URL}/api/categories`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => {
        if (!r.ok) throw new Error(`Status ${r.status}`);
        return r.json();
      })
      .then(data => {
        setOpts({
          lojas: Array.isArray(data.lojas) ? data.lojas : [],
          contatos: Array.isArray(data.contatos) ? data.contatos : [],
          ocorrencias: Array.isArray(data.ocorrencias) ? data.ocorrencias : []
        });
      })
      .catch(err => {
        console.error('Erro ao carregar categorias:', err);
        setOpts({ lojas: [], contatos: [], ocorrencias: [] });
      });
  }, [token]);

  // preenche form ao editar, ou reseta campos
  useEffect(() => {
    if (editingAtendimento) {
      setForm({
        atendente: editingAtendimento.atendente,
        dia: editingAtendimento.dia,
        horaInicio: editingAtendimento.horaInicio,
        horaFim: editingAtendimento.horaFim,
        loja: editingAtendimento.loja,
        contato: editingAtendimento.contato,
        ocorrencia: editingAtendimento.ocorrencia
      });
    } else {
      setForm(f => ({
        ...f,
        dia: today,
        horaInicio: '',
        horaFim: '',
        loja: '',
        contato: '',
        ocorrencia: ''
      }));
    }
  }, [editingAtendimento, today]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (form.horaFim <= form.horaInicio) {
      alert('A hora de fim deve ser depois da hora de início.');
      return;
    }
    const payload = {
      atendente: form.atendente,
      dia: form.dia,
      horaInicio: form.horaInicio,
      horaFim: form.horaFim,
      loja: form.loja,
      contato: form.contato,
      ocorrencia: form.ocorrencia
    };
    const isEdit = Boolean(editingAtendimento);
    const url = isEdit
      ? `${API_URL}/api/atendimentos/${editingAtendimento.id}`
      : `${API_URL}/api/atendimentos`;
    const method = isEdit ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify(payload)
    })
      .then(async r => {
        await assertOk(r, 'Erro ao salvar atendimento.');
        if (isEdit) onUpdate && onUpdate();
        else onAdd && onAdd();
        clearEditing();
        setForm(f => ({
          ...f,
          dia: today,
          horaInicio: '',
          horaFim: '',
          loja: '',
          contato: '',
          ocorrencia: ''
        }));
      })
      .catch(err => alert(err.message || 'Erro ao salvar atendimento.'));
  };

  return (
    <Paper elevation={0} sx={{ width: '100%' }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2.5 }}>
          {editingAtendimento ? 'Editar Atendimento' : 'Cadastrar Atendimento'}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              name="atendente"
              label="Atendente"
              value={form.atendente}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              name="dia"
              label="Data"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={form.dia}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField
              fullWidth
              name="horaInicio"
              label="Hora Início"
              type="time"
              required
              InputLabelProps={{ shrink: true }}
              value={form.horaInicio}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField
              fullWidth
              name="horaFim"
              label="Hora Fim"
              type="time"
              required
              InputLabelProps={{ shrink: true }}
              value={form.horaFim}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              fullWidth
              autoHighlight
              options={(opts.lojas || []).map(({ value }) => value)}
              value={form.loja || null}
              onChange={(_, newValue) =>
                setForm(f => ({ ...f, loja: newValue || '' }))
              }
              noOptionsText="Nenhuma loja encontrada"
              renderInput={params => (
                <TextField {...params} name="loja" label="Loja" required />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              fullWidth
              autoHighlight
              options={(opts.contatos || [])
                .filter(c => c.active)
                .map(({ value }) => value)}
              value={form.contato || null}
              onChange={(_, newValue) =>
                setForm(f => ({ ...f, contato: newValue || '' }))
              }
              noOptionsText="Nenhum contato encontrado"
              renderInput={params => (
                <TextField {...params} name="contato" label="Contato" required />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              fullWidth
              autoHighlight
              options={(opts.ocorrencias || []).map(({ value }) => value)}
              value={form.ocorrencia || null}
              onChange={(_, newValue) =>
                setForm(f => ({ ...f, ocorrencia: newValue || '' }))
              }
              noOptionsText="Nenhuma ocorrência encontrada"
              renderInput={params => (
                <TextField {...params} name="ocorrencia" label="Ocorrência" required />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: 'auto' }, px: 4 }}
            >
              {editingAtendimento ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
